import format from '@/common/utilities/format';

export function getIconLabel(label, changed, icon) {
    if (!changed && icon) {
        return `<i class="hidden-xs ${icon}"></i> ${label}`;
    }
    return label;
}

function resolveUnitLabels(molang) {
    if (typeof molang !== 'function') {
        return { sqft: 'Sqft', acre: 'Acre' };
    }
    return {
        sqft: molang('search.filter.units.sqft') || 'Sqft',
        acre: molang('search.filter.units.acre') || 'Acre',
    };
}

export function getFormatValue(value, input, isEndWithPlus, molang) {
    if (input === 'mvtInputCurrency') {
        if (isEndWithPlus) {
            return `$${format.friendlyPrice(value)}+`;
        }
        return `$${format.friendlyPrice(value)}`;
    } else if (input === 'mvtInputSqft') {
        const unitLabels = resolveUnitLabels(molang);
        if (value > 0) {
            return format.formatFriendlyLotSize(value, null, isEndWithPlus, unitLabels);
        }
        return `0 ${unitLabels.sqft}`;
    } else if (input !== 'mvtInputInt') {
        if (isEndWithPlus) {
            return `${format.formatNumberByComma(value)}+`;
        }
        return format.formatNumberByComma(value);
    }

    if (isEndWithPlus) {
        return `${value}+`;
    }
    return value;
}

export function isChanged(value, defaultValue) {
    if (defaultValue) {
        if (Array.isArray(value)) {
            return value[0] != defaultValue[0] || value[1] != defaultValue[1];
        }
        return value != defaultValue;
    }

    if (Array.isArray(value)) {
        return value.length > 0;
    }
    return !!value;
}

export function getLabel(value, defaultValue, changed, filter, molang) {
    let label;
    if (changed) {
        if (defaultValue && defaultValue.length === 2) {
            let input = filter.options.input || '';
            let min = value[0] * 1;
            let max = value[1] * 1;
            if (filter.options && filter.options.extraData && filter.options.extraData.value[0] === min && filter.options.extraData.value[1] === max) {
                label = filter.options.extraData.text;
            } else if (min != defaultValue[0] && max == defaultValue[1]) {
                label = `${getFormatValue(min, input, true, molang)} ${filter.label}`;
            } else if (min == defaultValue[0] && max != defaultValue[1]) {
                const upTo = typeof molang === 'function' ? molang('search.filter.upTo') || 'Up to' : 'Up to';
                label = `${upTo} ${getFormatValue(max, input, false, molang)} ${filter.label}`;
            } else if (min === max) {
                label = `${getFormatValue(min, input, false, molang)} ${filter.label}`;
            } else {
                label = `${getFormatValue(min, input, false, molang)} - ${getFormatValue(max, input, false, molang)} ${filter.label}`;
            }
        } else if (Array.isArray(value) && value.length > 0) {
            label = `${filter.label} <i class="filter-count" >${value.length}</i>`;
        } else {
            if (filter.data) {
                let item = filter.data.find((item) => item.value == value);
                if (item) {
                    label = item.labelText || item.text;
                }
            }
            if (!label) {
                label = filter.label;
            }
        }
    } else {
        label = filter.label;
    }

    return getIconLabel(label, changed, filter.icon);
}

/**
 * Pure reducer for building the collapsed "filter chips" label list.
 *
 * This is extracted from `mvt-searchfilter.js` so the same logic can be reused
 * by the Vue component (CSR) and Vuex SSR initialization (mutations).
 *
 * Note: `filter.label` / `filter.exposeItem` callbacks may be defined with
 * lexical `this` in the component's `data()`; this function just invokes them.
 */
export function generateFilterLabel({ searchFilterData, filterModel, filterValue, isRent, isSold, molang }) {
    let labels = [];

    searchFilterData.reduce((arr, filter) => {
        let value = filterModel[filter.model];
        let defaultValue = filter.options ? filter.options.defaultValue || '' : '';
        let changed = false;
        let label;

        if (defaultValue && defaultValue.length === 2) {
            if (filter.options && filter.options.mapping) {
                value = [filterModel[filter.model][filter.options.mapping.min], filterModel[filter.model][filter.options.mapping.max]];
            }
        } else if (Array.isArray(value) && value.length > 0) {
            if (filter.data && filter.data.length > 0) {
                value = value.filter((item) => filter.data.find((e) => (e.value || e) === item));
            }
        }

        changed = isChanged(value, defaultValue);

        if (typeof filter.label === 'function') {
            label = filter.label(value, defaultValue, changed, filter);
        } else {
            label = getLabel(value, defaultValue, changed, filter, molang);
        }

        let hide = false;
        if (isRent) {
            if (['houseMedia', 'houseStates', 'lotSize', 'hoa'].includes(filter.id)) {
                hide = true;
            }
        } else if (isSold) {
            if (['houseStates'].includes(filter.id)) {
                hide = true;
            }
        }

        // merge bed and bath
        if (filter.merge) {
            let item = arr.find((e) => e.id === filter.merge);
            item.hide = hide || item.hide;
            item.order = filter.order || item.order;
            item.id += `_${filter.id}`;
            item.changed = changed || item.changed;
            item.label += ` / ${label}`;
            item.merge = true;
            item.count += changed ? 1 : 0;
            return arr;
        }

        // expose rental
        if (filter.expose && filter.data && filter.data.length > 0) {
            filter.data.forEach((item) => {
                arr.push({
                    hide: hide,
                    order: filter.order || '',
                    id: `${filter.id}-${item.value}`,
                    changed: value && value.includes(item.value),
                    label: item.text,
                    expose: true,
                });
            });
            if (filter.exposeItem && typeof filter.exposeItem === 'function') {
                let exItem = filter.exposeItem(value, defaultValue, changed, filter, filterValue);
                if (exItem) {
                    arr.push(exItem);
                }
            }
            return arr;
        }

        arr.push({
            hide: hide,
            order: filter.order || '',
            id: filter.id,
            changed: changed,
            label: label,
            count: changed ? 1 : 0,
        });
        return arr;
    }, labels);

    return labels;
}
